import m from 'mithril';

import {
  CreateUserError,
  CreateUserErrorActionCode,
  createUser,
} from '../services/auth';
import {
  hasNoErrors,
  validateSignup,
  type FormErrors,
  type SignupValues,
} from '../services/forms';
import { runtimeUrls } from '../services/env';
import { captureException } from '../services/sentry';
import { Alert } from '../components/Alert';
import { Button } from '../components/Button';
import { TextField } from '../components/FormControls';
import { Loading } from '../components/Loading';
import { Modal } from '../components/Modal';
import { Type } from '../components/Typography';

/**
 * Signup route attrs.
 */
export interface SignupAttrs extends m.Attributes {
  /** Optional referral code. */
  ref?: string;
}

/**
 * Signup page.
 */
export const Signup: m.ClosureComponent<SignupAttrs> = () => {
  const state = {
    values: {
      firstname: '',
      lastname: '',
      email: '',
      password: '',
    } satisfies SignupValues,
    errors: {} as FormErrors<SignupValues>,
    processing: false,
    fatalError: '',
  };

  const submit = (refCode: string | undefined): void => {
    state.errors = validateSignup(state.values);
    state.fatalError = '';
    if (!hasNoErrors(state.errors)) {
      return;
    }

    state.processing = true;
    void createUser({
      firstname: state.values.firstname,
      lastname: state.values.lastname,
      email: state.values.email,
      referralCode: refCode,
    }, state.values.password)
      .then(() => {
        m.route.set('/download/newUser');
      })
      .catch((error: unknown) => {
        state.processing = false;
        if (error instanceof CreateUserError) {
          if (error.code === CreateUserErrorActionCode.WeakPassword) {
            state.errors.password = 'This password is too weak.';
          } else if (error.code === CreateUserErrorActionCode.AccountAlreadyExists) {
            state.errors.email = 'An account with this email already exists.';
          }
        } else {
          captureException(error);
          console.error('failed to create user:', error);
          state.fatalError = `Try your request again, or contact ${runtimeUrls.supportEmail}`;
        }
        m.redraw();
      });
  };

  return {
    view: ({ attrs }) => {
      return m('section', { class: 'page signup-page' }, [
        m(Modal, { open: state.processing, label: 'Creating account' }, [
          m('div', { class: 'modal-loading' }, [
            m(Type, { size: 'subtitle' }, 'Creating account...'),
            m(Loading),
          ]),
        ]),
        m('div', { class: 'signup-page__layout' }, [
          m('div', { class: 'form-panel' }, [
            state.fatalError
              ? m(Alert, {
                title: 'Something went wrong!',
                message: state.fatalError,
                variant: 'danger',
              })
              : [
                m(Type, { className: 'form-intro' }, [
                  'Already have an account?',
                  m('a', {
                    href: runtimeUrls.openerURL,
                    target: '_blank',
                    rel: 'noreferrer',
                  }, ' Launch MeshOS'),
                ]),
                m('form', {
                  class: 'stacked-form',
                  onsubmit: (event: SubmitEvent) => {
                    event.preventDefault();
                    submit(attrs.ref);
                  },
                }, [
                  m(TextField, {
                    label: 'First Name',
                    name: 'firstname',
                    placeholder: 'Jack',
                    value: state.values.firstname,
                    hint: state.errors.firstname,
                    autocomplete: 'off',
                    onInput: (value) => {
                      state.values.firstname = value;
                    },
                  }),
                  m(TextField, {
                    label: 'Last Name',
                    name: 'lastname',
                    placeholder: 'Sparrow',
                    value: state.values.lastname,
                    hint: state.errors.lastname,
                    autocomplete: 'off',
                    onInput: (value) => {
                      state.values.lastname = value;
                    },
                  }),
                  m(TextField, {
                    label: 'Email',
                    name: 'email',
                    type: 'email',
                    placeholder: 'rumlover@pirate.net',
                    value: state.values.email,
                    hint: state.errors.email,
                    autocomplete: 'off',
                    onInput: (value) => {
                      state.values.email = value;
                    },
                  }),
                  m(TextField, {
                    label: 'Password',
                    name: 'password',
                    type: 'password',
                    value: state.values.password,
                    hint: state.errors.password,
                    autocomplete: 'new-password',
                    onInput: (value) => {
                      state.values.password = value;
                    },
                  }),
                  m('div', { class: 'form-actions' }, [
                    m(Button, { type: 'submit', disabled: state.processing }, 'Sign Up'),
                  ]),
                  m(Type, { className: 'form-footnote' }, [
                    'By creating an account, you agree to the ',
                    m(m.route.Link, { href: '/terms' }, 'Terms of Service'),
                  ]),
                ]),
              ],
          ]),
          m('aside', { class: 'requirements-panel' }, [
            m(Type, { size: 'subheading' }, 'Minimum Requirements:'),
            m(Type, { size: 'subtext' }, 'macOS Big Sur (11.3)'),
            m(Type, { size: 'subheading' }, 'Recommended:'),
            m(Type, { size: 'subtext' }, 'macOS Ventura (13) or later'),
            m(Type, { size: 'subtext' }, 'M1 chip'),
            m(Type, { size: 'subtext' }, '16 GB of memory'),
          ]),
        ]),
      ]);
    },
  };
};
